import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  MinusCircleIcon
} from '@heroicons/react/24/outline';
import { inventoryAPI, filesAPI, downloadFile, formatError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const InventorySheet = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [headers] = useState([
    'Product ID',
    'Product Name',
    'Current Stock',
    'Min Stock Level',
    'Max Stock Level',
    'Unit Cost',
    'Supplier',
    'Category',
    'Location',
    'Stock Status'
  ]);

  const loadInventoryData = useCallback(async () => {
    try {
      const params = showLowStockOnly ? { low_stock_only: true } : {};
      const response = await inventoryAPI.getAll(params);
      setInventoryData(response.data);
    } catch (error) {
      toast.error('Failed to load inventory data');
      console.error('Load inventory error:', error);
    } finally {
      setLoading(false);
    }
  }, [showLowStockOnly]);

  // Load inventory data on component mount
  useEffect(() => {
    loadInventoryData();
  }, [loadInventoryData]);

  // Auto-save functionality
  const autoSave = useCallback(async (updatedData) => {
    setSaving(true);
    try {
      // Prepare batch update data
      const updates = updatedData.map(item => ({
        id: item.id,
        data: {
          product_id: item.product_id,
          product_name: item.product_name,
          current_stock: parseInt(item.current_stock) || 0,
          min_stock_level: parseInt(item.min_stock_level) || 10,
          max_stock_level: parseInt(item.max_stock_level) || null,
          unit_cost: parseFloat(item.unit_cost) || null,
          supplier: item.supplier || '',
          category: item.category || '',
          location: item.location || '',
          custom_headers: item.custom_headers || {}
        }
      }));

      await inventoryAPI.batchUpdate({
        sheet_type: 'inventory',
        updates: updates
      });

      toast.success('Changes saved automatically', { duration: 2000 });
    } catch (error) {
      toast.error('Auto-save failed');
      console.error('Auto-save error:', error);
    } finally {
      setSaving(false);
    }
  }, []);

  // Handle cell editing
  const handleCellEdit = (rowIndex, field, value) => {
    const updatedData = [...inventoryData];

    if (['current_stock', 'min_stock_level', 'max_stock_level'].includes(field)) {
      updatedData[rowIndex][field] = parseInt(value) || 0;
    } else if (field === 'unit_cost') {
      updatedData[rowIndex][field] = parseFloat(value) || 0;
    } else {
      updatedData[rowIndex][field] = value;
    }

    setInventoryData(updatedData);

    // Trigger auto-save after a delay
    setTimeout(() => {
      autoSave(updatedData);
    }, 1000);
  };

  // Add new row
  const addNewRow = async () => {
    try {
      const newItem = {
        product_id: '',
        product_name: '',
        current_stock: 0,
        min_stock_level: 10,
        max_stock_level: 100,
        unit_cost: 0,
        supplier: '',
        category: '',
        location: ''
      };

      const response = await inventoryAPI.create(newItem);
      setInventoryData([...inventoryData, response.data]);
      toast.success('New item added');
    } catch (error) {
      toast.error('Failed to add new item');
    }
  };

  // Delete selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.size === 0) {
      toast.error('No rows selected');
      return;
    }

    try {
      const deletePromises = Array.from(selectedRows).map(id =>
        inventoryAPI.delete(id)
      );

      await Promise.all(deletePromises);

      setInventoryData(inventoryData.filter(item => !selectedRows.has(item.id)));
      setSelectedRows(new Set());
      toast.success(`${selectedRows.size} item(s) deleted`);
    } catch (error) {
      toast.error('Failed to delete items');
    }
  };

  // Stock adjustment
  const adjustStock = async (id, adjustment) => {
    try {
      const response = await inventoryAPI.adjustStock(id, adjustment, 'Manual adjustment');

      // Update local data
      setInventoryData(prevData =>
        prevData.map(item =>
          item.id === id
            ? { ...item, current_stock: response.data.new_stock }
            : item
        )
      );

      toast.success(`Stock adjusted by ${adjustment}`);
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      toast.loading('Importing file...', { id: 'file-upload' });

      const response = await filesAPI.uploadInventory(file);

      toast.success(
        `${response.data.records_imported} records imported successfully`,
        { id: 'file-upload' }
      );

      // Reload data
      loadInventoryData();
    } catch (error) {
      toast.error(formatError(error), { id: 'file-upload' });
    }

    // Reset file input
    event.target.value = '';
  };

  // Export data
  const handleExport = async (format) => {
    try {
      toast.loading(`Exporting ${format.toUpperCase()}...`, { id: 'export' });

      const response = await filesAPI.exportData({
        sheet_type: 'inventory',
        format: format
      });

      const filename = `inventory_export_${new Date().toISOString().split('T')[0]}.${format}`;
      downloadFile(response.data, filename);

      toast.success('Export completed', { id: 'export' });
    } catch (error) {
      toast.error('Export failed', { id: 'export' });
    }
  };

  // Download template
  const downloadTemplate = async () => {
    try {
      const response = await filesAPI.downloadTemplate('inventory');
      downloadFile(response.data, 'inventory_template.csv');
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Row selection
  const toggleRowSelection = (id) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedRows(newSelection);
  };

  // Select all rows
  const toggleSelectAll = () => {
    if (selectedRows.size === inventoryData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(inventoryData.map(item => item.id)));
    }
  };

  // Get stock status
  const getStockStatus = (item) => {
    if (item.current_stock <= item.min_stock_level) {
      return { status: 'Low Stock', color: 'text-red-600 bg-red-50' };
    } else if (item.current_stock >= (item.max_stock_level || item.min_stock_level * 3)) {
      return { status: 'Overstock', color: 'text-yellow-600 bg-yellow-50' };
    }
    return { status: 'Good', color: 'text-green-600 bg-green-50' };
  };

  if (loading) {
    return <LoadingSpinner text="Loading inventory data..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Sheet</h1>
          <p className="text-gray-600">Manage your hotel inventory in an Excel-like interface</p>
        </div>

        <div className="flex items-center space-x-4">
          {saving && (
            <div className="flex items-center text-blue-600">
              <LoadingSpinner size="sm" text="Auto-saving..." center={false} />
            </div>
          )}

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showLowStockOnly}
              onChange={(e) => setShowLowStockOnly(e.target.checked)}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500 mr-2"
            />
            <span className="text-sm text-gray-700">Show low stock only</span>
          </label>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          {/* Add Row */}
          <button
            onClick={addNewRow}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Item
          </button>

          {/* Delete Selected */}
          <button
            onClick={deleteSelectedRows}
            disabled={selectedRows.size === 0}
            className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete Selected ({selectedRows.size})
          </button>

          {/* File Upload */}
          <label className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            Import File
            <input
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
            />
          </label>

          {/* Export Dropdown */}
          <div className="relative inline-block text-left">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleExport(e.target.value);
                  e.target.value = '';
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <option value="">Export As...</option>
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="csv">CSV (.csv)</option>
            </select>
          </div>

          {/* Download Template */}
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Template
          </button>
        </div>
      </div>

      {/* Excel-like Data Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full excel-grid">
            <thead>
              <tr className="bg-gray-50">
                <th className="excel-header-cell w-12">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === inventoryData.length && inventoryData.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </th>
                <th className="excel-header-cell w-16">Actions</th>
                {headers.map((header, index) => (
                  <th key={index} className="excel-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inventoryData.map((row, rowIndex) => {
                const stockStatus = getStockStatus(row);
                return (
                  <tr key={row.id} className={selectedRows.has(row.id) ? 'bg-green-50' : 'hover:bg-gray-50'}>
                    <td className="excel-cell w-12">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(row.id)}
                        onChange={() => toggleRowSelection(row.id)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                    </td>

                    <td className="excel-cell w-16">
                      <div className="flex space-x-1">
                        <button
                          onClick={() => adjustStock(row.id, -1)}
                          className="text-red-600 hover:text-red-800"
                          title="Decrease stock"
                        >
                          <MinusCircleIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => adjustStock(row.id, 1)}
                          className="text-green-600 hover:text-green-800"
                          title="Increase stock"
                        >
                          <PlusCircleIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>

                    <td className="excel-cell">
                      <input
                        type="text"
                        value={row.product_id || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'product_id', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="Auto-generated"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="text"
                        value={row.product_name || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'product_name', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="Product name"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="number"
                        value={row.current_stock || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'current_stock', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="0"
                        min="0"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="number"
                        value={row.min_stock_level || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'min_stock_level', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="10"
                        min="0"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="number"
                        value={row.max_stock_level || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'max_stock_level', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="100"
                        min="0"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="number"
                        step="0.01"
                        value={row.unit_cost || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'unit_cost', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="0.00"
                        min="0"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="text"
                        value={row.supplier || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'supplier', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="Supplier name"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="text"
                        value={row.category || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'category', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="Category"
                      />
                    </td>

                    <td className="excel-cell">
                      <input
                        type="text"
                        value={row.location || ''}
                        onChange={(e) => handleCellEdit(rowIndex, 'location', e.target.value)}
                        className="w-full bg-transparent border-0 outline-none p-1"
                        placeholder="Location"
                      />
                    </td>

                    <td className="excel-cell">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                        {row.current_stock <= row.min_stock_level && (
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                        )}
                        {stockStatus.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Total Products</div>
          <div className="text-2xl font-bold text-gray-900">{inventoryData.length}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Total Stock Value</div>
          <div className="text-2xl font-bold text-gray-900">
            ${inventoryData.reduce((sum, item) => sum + (item.current_stock * (item.unit_cost || 0)), 0).toFixed(2)}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Low Stock Items</div>
          <div className="text-2xl font-bold text-red-600">
            {inventoryData.filter(item => item.current_stock <= item.min_stock_level).length}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Total Items in Stock</div>
          <div className="text-2xl font-bold text-gray-900">
            {inventoryData.reduce((sum, item) => sum + (item.current_stock || 0), 0)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventorySheet;